import {
  onSnapshot,
  type CollectionReference, type DocumentData, type FirestoreError, type Query, type QuerySnapshot,
} from "firebase/firestore";

// Signing out (or the anonymous->verified auth transition) invalidates the
// current credential while listeners from the previous session are still
// attached; Firestore rejects them with permission-denied before React has
// re-rendered and torn them down. That's expected, not a bug — swallow it
// here instead of letting the SDK log it as an "uncaught" listener error.
export function ignoreTransientSnapshotError(err: { code?: string }) {
  if (err.code !== "permission-denied") {
    console.error(err);
  }
}

// A baby doc created via addDoc() can still be mid-commit on the backend when a
// listener for one of its subcollections (logEvents, tasks, dailyStats, milestones)
// is first registered: the owning babies-list listener reacts to the doc's local,
// optimistic write and selects it as active well before the write's round-trip
// resolves, so the subcollection query races the parent doc's commit. Security
// rules resolve that race as "not found" -> permission-denied, which is recoverable
// (not a real access problem), so retry a few times with backoff instead of leaving
// the listener dead until the next page load.
export function subscribeWithPermissionRetry(
  target: Query<DocumentData> | CollectionReference<DocumentData>,
  onNext: (snapshot: QuerySnapshot<DocumentData>) => void
): () => void {
  let cancelled = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  let unsub = () => {};

  function subscribe() {
    unsub = onSnapshot(target, onNext, onError);
  }

  function onError(err: FirestoreError) {
    if (err.code === "permission-denied" && !cancelled && attempt < 3) {
      const delayMs = 500 * 2 ** attempt;
      attempt += 1;
      retryTimer = setTimeout(() => { if (!cancelled) subscribe(); }, delayMs);
      return;
    }
    ignoreTransientSnapshotError(err);
  }

  subscribe();
  return () => {
    cancelled = true;
    if (retryTimer) clearTimeout(retryTimer);
    unsub();
  };
}
